/**
 * Feedbackpunten uit een klantmail bij een v2/DEF-taak (20 okt 2026).
 *
 * Engine: /api/v1/feedback-punten (Bearer; de gebruiker komt uit het token, nooit uit de body).
 * Opslaan = één import per mail per taak; ongedaan maken haalt die import (of de aangevulde punten)
 * weer weg, zolang alle punten nog open zijn. Afvinken zet de status van één punt.
 */
import { AI_ENGINE_URL } from './apiConfig';
import { authFetch } from './authHelpers';
import { leesJson } from './apiFout';
import { biedOngedaanAan } from './ongedaan';
import { schoonMailTekst, splitsMail } from './mailTekst';

const API = `${AI_ENGINE_URL}/api/v1/feedback-punten`;
const JSON_KOP = { 'Content-Type': 'application/json' };

// Zelfde regel als core/taken.py vereist_feedback en backend/routes/dagstart.js.
// (Zonder lookbehind, zodat ook oudere Safari's hem slikken.)
const FEEDBACK_NAAM = /(?:^|[^A-Za-z0-9])(?:[vV]\s?(?:[2-9]|[1-9]\d)|[Vv]ersie\s?(?:[2-9]|[1-9]\d)|DEF|def)(?![A-Za-z0-9])/;
const GEEN_FEEDBACK_TYPES = new Set(['Afspraak', 'Draaidag', 'Vrij/intern', 'Vanallesuurtje', 'Deadline', 'Archief']);
export const vereistFeedback = (naam, type) => !(type && GEEN_FEEDBACK_TYPES.has(String(type)))
  && FEEDBACK_NAAM.test(String(naam || ''));

const eerste = (v) => (Array.isArray(v) ? v[0] : v) || null;
const tekstVan = (v) => (Array.isArray(v) ? v.filter(Boolean).join(', ') : String(v || ''));

/** v2/DEF-taken van dit project, gegroepeerd per deliverable (open taken eerst). */
export function kandidaatTaken(tasks = [], deliverables = []) {
  const naamVan = new Map((deliverables || []).map((d) => [d.id,
    d.fields?.['Naam van Deliverable'] || d.fields?.Deliverable || 'Deliverable']));
  const groepen = new Map();
  (tasks || []).forEach((t) => {
    const f = t.fields || {};
    if (!t.id || !vereistFeedback(f.NaamTaak, f.Type)) return;
    const delId = eerste(f.Deliverable);
    const sleutel = delId || '_zonder';
    if (!groepen.has(sleutel)) {
      groepen.set(sleutel, { deliverableId: delId, deliverableNaam: delId ? (naamVan.get(delId) || 'Deliverable') : 'Zonder deliverable', taken: [] });
    }
    groepen.get(sleutel).taken.push({
      id: t.id,
      naam: f.NaamTaak || 'Taak',
      deliverableId: delId,
      wie: tekstVan(f['Voornaam Assignee']),
      start: String(f.StartDateUitvoerder || '').slice(0, 10) || null,
      afgerond: f.PrioNr === 'Afgerond' || f.Afgerond === true,
    });
  });
  const lijst = [...groepen.values()];
  lijst.forEach((g) => g.taken.sort((a, b) => (a.afgerond - b.afgerond) || String(b.start || '').localeCompare(String(a.start || ''))));
  return lijst.sort((a, b) => (a.deliverableId ? 0 : 1) - (b.deliverableId ? 0 : 1) || a.deliverableNaam.localeCompare(b.deliverableNaam));
}

/** Tekst die standaard naar de AI gaat: alleen het eigen bericht, zonder handtekening en eerdere berichten. */
export function tekstVoorAi(body, { onderwerp, metEerder = false } = {}) {
  const ruw = String(body || '');
  if (!ruw.trim()) return '';
  const g = splitsMail(ruw, { onderwerp });
  const delen = [g.eigen];
  if (g.doorgestuurd) delen.push(g.doorgestuurd.tekst);
  if (metEerder && g.citaat) delen.push(g.citaat);
  const tekst = delen.filter(Boolean).join('\n\n').trim();
  return tekst || (g.citaat || schoonMailTekst(ruw)).trim();
}

/** Afbeeldingen of bijlagen in de mail gaan niet mee: bij de punten staat dan "zie mail". */
export const heeftBeeldInMail = (mail, body) => Boolean(mail?.hasAttachments)
  || /\[cid:|\[image|<img|\.(png|jpe?g|gif|heic)\b/i.test(String(body || ''));

/** Bron-id voor het ontdubbelen: internetMessageId is in elke mailbox gelijk; anders de Graph-id. */
export const bronVanMail = (mail) => ({
  bronBerichtId: mail?.internetMessageId || mail?.id || '',
  bronIdZeker: Boolean(mail?.internetMessageId),
});

export const formatTijd = (sec) => {
  if (sec === null || sec === undefined || sec === '' || Number.isNaN(Number(sec))) return '';
  const s = Math.max(0, Math.round(Number(sec)));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// ── API ────────────────────────────────────────────────────────────────────
export async function ontleedTekst(tekst, projectNumber = null) {
  // projectNumber: EU-verwerking — bij een EU-klant weigert de server (409) i.p.v. naar een VS-model te sturen
  const r = await authFetch(`${API}/ontleed`, { method: 'POST', headers: JSON_KOP, body: JSON.stringify({ tekst, project_number: projectNumber }) });
  return leesJson(r, 'Punten maken');
}

export async function leesFeedbackPunten(taskIds) {
  const ids = [...new Set((taskIds || []).filter(Boolean))];
  if (!ids.length) return {};
  const r = await authFetch(`${API}?task_ids=${encodeURIComponent(ids.join(','))}`);
  return leesJson(r, 'Feedbackpunten laden');
}

export async function zetPuntStatus(puntId, status) {
  const r = await authFetch(`${API}/${encodeURIComponent(puntId)}`, { method: 'PATCH', headers: JSON_KOP, body: JSON.stringify({ status }) });
  return leesJson(r, 'Punt bijwerken');
}

async function verwijderImport(importId, puntIds) {
  const extra = puntIds ? `?punten=${encodeURIComponent(puntIds.join(','))}` : '';
  const r = await authFetch(`${API}/import/${encodeURIComponent(importId)}${extra}`, { method: 'DELETE' });
  return leesJson(r, 'Ongedaan maken');
}

/**
 * Opslaan met vangnet. 409 'al_gekoppeld' komt terug als { alGekoppeld: true } zodat de dialoog
 * kan vragen of de punten bij de bestaande import moeten. Na succes: undo-toast (alleen de maker,
 * alleen zolang alles open is — de server bewaakt dat).
 */
export async function slaFeedbackOp(body, { taakNaam, onTeruggedraaid } = {}) {
  const r = await authFetch(API, { method: 'POST', headers: JSON_KOP, body: JSON.stringify(body) });
  if (r.status === 409) {
    const d = await r.clone().json().catch(() => ({}));
    if (d?.detail?.code === 'al_gekoppeld') return { alGekoppeld: true, importId: d.detail.importId };
  }
  const data = await leesJson(r, 'Feedback opslaan');
  const importId = data.import?.id;
  const puntIds = data.aangevuld ? data.nieuwePuntIds : null;
  const n = (data.nieuwePuntIds || []).length;
  biedOngedaanAan(`${n} feedbackpunt${n === 1 ? '' : 'en'} bij "${String(taakNaam || 'de taak').slice(0, 50)}"`, async () => {
    await verwijderImport(importId, puntIds);
    onTeruggedraaid?.(data);
  }, { eigenKnop: true });
  return data;
}
